import { Button } from '../Button';
import React, { InputHTMLAttributes } from 'react';
import {
  DefaultValues,
  FieldErrors,
  FieldValues,
  RegisterOptions,
  SubmitHandler,
  useForm,
  UseFormRegister,
} from 'react-hook-form';

interface FormProps<T extends FieldValues> {
  edit: boolean;
  canDelete?: boolean;
  onFinish: SubmitHandler<T>;
  handleDelete?: () => void;
  defaultValues?: DefaultValues<T>;
  children: any;
}

export function Form<T extends FieldValues>({
  edit,
  onFinish,
  handleDelete,
  defaultValues,
  children,
  canDelete,
}: FormProps<T>) {
  const methods = useForm<T>({
    defaultValues,
  });
  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  return (
    <form
      onSubmit={handleSubmit(onFinish)}
      className="w-full flex flex-col flex-nowrap mt-8"
    >
      {React.Children.map(children, (child) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        child.props.name
          ? React.createElement(child.type, {
              ...{
                ...child.props,
                register: methods.register,
                errors,
                key: child.props.name,
              },
            })
          : child;
      })}
      <div
        className={`inline-flex ${
          edit ? 'flex-row justify-between' : 'flex-col items-center'
        } mt-6`}
      >
        {edit && canDelete ? (
          <Button
            type={'link'}
            htmlType={'button'}
            onClick={() => (handleDelete ? handleDelete() : null)}
          >
            Delete Entry
          </Button>
        ) : null}
        <Button type={'default'} htmlType={'submit'}>
          Save
        </Button>
      </div>
    </form>
  );
}

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors;
  options?: RegisterOptions;
}

Form.Input = function Input({
  id,
  label,
  register,
  errors,
  name,
  options,
  ...rest
}: InputProps) {
  return (
    <>
      <div className="inline-flex flex-row flex-nowrap items-stcenter my-2 justify-between">
        <label className="font-semibold text-primary-200 text-sm" htmlFor={id}>
          {label}
        </label>
        <input
          placeholder={label}
          className={`w-64 border rounded-lg text-left text-primary-200 font-normal text-sm px-2 py-1 ${
            errors[id] && 'border-danger focus:border-danger bg-danger/5'
          }`}
          type="text"
          id={id}
          {...register(id, { ...options })}
          {...rest}
        />
      </div>
      {errors[id] && (
        <span className="text-right text-danger text-sm font-normal">
          This field is required
        </span>
      )}
    </>
  );
};

interface TextAreaProps extends InputHTMLAttributes<HTMLTextAreaElement> {
  id: string;
  label: string;
  register: UseFormRegister<FieldValues>;
  errors: FieldErrors;
  options?: RegisterOptions;
}

Form.TextArea = function TextArea({
  id,
  label,
  register,
  errors,
  name,
  options,
  ...rest
}: TextAreaProps) {
  return (
    <>
      <div className="inline-flex flex-col flex-nowrap items-start my-2 justify-between">
        <label className="font-semibold text-primary-200 text-sm" htmlFor={id}>
          {label}
        </label>
        <textarea
          placeholder={label}
          className={`w-full border rounded-lg text-left text-primary-200 font-normal text-sm my-2 px-2 py-1 ${
            errors[id] && 'border-danger focus:border-danger bg-danger/5'
          }`}
          id={id}
          {...register(id, { ...options })}
          {...rest}
        />
      </div>
      {errors[id] && (
        <span className="text-right text-danger text-sm font-normal">
          This field is required
        </span>
      )}
    </>
  );
};
